<template>
  <footer class="footer">
    <div class="footer-content">
      <!-- 关注我们 -->
      <div class="footer-section">
        <h3 class="section-title-one">关注我们</h3>
        <div class="social-icons">
          <div 
            v-for="item in socialMediaConfig" 
            :key="item.key"
            :ref="el => setSocialIconRef(el, item.key)"
            class="social-icon"
            :class="item.key"
            @click="handleSocialClick(item, $event)"
          >
            <img 
              :src="$fixAssetUrl(getSocialIconPath(item.key))" 
              :alt="item.title" 
            />
          </div>
        </div>
      </div>
      
      <!-- 二维码 Popover 弹窗 -->
      <QrcodePopup
        v-model:show="showQrcodePopup"
        :qrcode-image="currentQrcodeImage"
        :title="currentQrcodeTitle"
        :trigger-element="triggerElement"
        @image-long-press="handleQrcodeLongPress"
      />
      
      <!-- 图片放大预览 -->
      <ImagePreview
        v-model:show="showImagePreview"
        :image-url="previewImageUrl"
        :title="previewImageTitle"
      />

      <!-- 联系我们 -->
      <div class="footer-section">
        <h3 class="section-title">联系我们</h3>
        <p class="working-hours">工作时间: 周一至周日 9:00–21:00</p>
        <div class="contact-buttons">
          <a href="tel:400-808-1648" class="contact-btn phone-btn">
            <!-- 电话图标内联 SVG -->
            <svg class="contact-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
              <path d="M980.16 752.64a718.848 718.848 0 0 0-83.2-66.56 893.568 893.568 0 0 0-92.032-56.96 166.4 166.4 0 0 0-75.84-26.88 68.16 68.16 0 0 0-55.872 33.28 288 288 0 0 0-25.152 49.92 216.128 216.128 0 0 1-18.496 36.48 286.144 286.144 0 0 1-77.76-41.6 906.688 906.688 0 0 1-114.56-93.44 870.848 870.848 0 0 1-93.12-114.56 288 288 0 0 1-41.984-77.44 218.944 218.944 0 0 1 36.864-18.56 307.2 307.2 0 0 0 49.344-25.6 66.88 66.88 0 0 0 33.664-55.68 167.808 167.808 0 0 0-26.816-75.52 913.664 913.664 0 0 0-56.896-92.16A693.312 693.312 0 0 0 271.296 44.16 109.568 109.568 0 0 0 199.104 0c-50.368 0-109.632 73.6-126.528 96.64a330.944 330.944 0 0 0-72.32 174.72 473.216 473.216 0 0 0 80.96 211.2 1472.64 1472.64 0 0 0 192.64 243.2l24.384 24.32v0.64a1473.92 1473.92 0 0 0 243.2 192 470.08 470.08 0 0 0 211.2 81.28 332.8 332.8 0 0 0 174.72-72.32c22.592-17.28 96.384-76.16 96.384-126.72a111.552 111.552 0 0 0-43.584-72.32z" fill="#ffffff"/>
            </svg>
            <span>电话咨询</span>
          </a>
          <button class="contact-btn online-btn" @click="openCustomerService">
            <!-- 在线咨询图标内联 SVG -->
            <svg class="contact-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
              <path d="M951.81937778 8.68352h-879.63875556c-34.95253333 0-63.49710222 28.54456889-63.49710222 63.49710222v668.75847111c0 34.95253333 28.54456889 63.49710222 63.49710222 63.49710222h255.15349333l206.21994667 211.46282667 165.44199111-211.46282667h253.40586667c34.95253333 0 63.49710222-28.54456889 63.49710222-63.49710222V72.18062222c-0.58254222-35.53507555-29.12711111-63.49710222-64.07964444-63.49710222zM442.67747555 327.33411555h140.97521778v140.97521778H442.67747555V327.33411555z m-251.07569777 0h140.97521777v140.97521778H191.60177778V327.33411555z m643.12661333 140.97521778h-140.97521778V327.33411555h140.97521778v140.97521778z" fill="#ffffff"/>
            </svg>
            <span>在线咨询</span>
          </button>
        </div>
      </div>

      <!-- 底部信息 -->
      <div class="footer-bottom">
        <div class="footer-info">
          <img 
            :src="$fixAssetUrl('/images/common/footer/police-badge.png')" 
            alt="警徽" 
            class="police-badge"
          />
          <span>沪ICP备13040679号-2 | Copyright @ 2013-2026</span>
        </div>
        <div class="footer-info">olacio.com All Rights Reserved</div>
        <div class="footer-info">上海云兜网络科技有限公司 版权所有</div>
        <div class="footer-info">地址: 上海市普陀区礼泉路689弄80号高尚领域T2 10楼</div>
      </div>
    </div>
  </footer>
</template>

<script setup>
// 客服行为统一在 CustomerService 中维护，页面只绑定咨询事件。
import { openCustomerService } from '@/utils/CustomerService'
import { ref, computed, onMounted } from 'vue'
import QrcodePopup from './QrcodePopup.vue'
import ImagePreview from './ImagePreview.vue'

/**
 * 社交媒体配置映射
 * 图标路径映射
 */
const socialIconMap = {
  'wechat': '/images/common/footer/wechat.png',
  'xiaohongshu': '/images/common/footer/xiaohongshu.png',
  'wechat-video': '/images/common/footer/other-social.png', // 视频号使用视频号图标显示
  'douyin': '/images/common/footer/douyin.png',
  'bilibili': '/images/common/footer/bilibili.png'
}

// 联系按钮图标已改为内联 SVG，不再需要外部文件路径

/**
 * Footer 配置
 */
const footerConfig = ref({
  socialMedia: []
})

/**
 * 二维码弹窗相关状态
 */
const showQrcodePopup = ref(false)
const currentQrcodeImage = ref('')
const currentQrcodeTitle = ref('扫码关注')
const triggerElement = ref(null)

/**
 * 图片预览相关状态
 */
const showImagePreview = ref(false)
const previewImageUrl = ref('')
const previewImageTitle = ref('')

/**
 * 社交媒体图标引用映射
 */
const socialIconRefs = new Map()

/**
 * 设置社交媒体图标引用
 */
const setSocialIconRef = (el, key) => {
  if (el) {
    socialIconRefs.set(key, el)
  }
}

/**
 * 计算属性：社交媒体配置
 */
const socialMediaConfig = computed(() => {
  return footerConfig.value.socialMedia || []
})

/**
 * 获取社交媒体图标路径
 * @param {String} key - 社交媒体 key
 * @returns {String} 图标路径
 */
const getSocialIconPath = (key) => {
  return socialIconMap[key] || '/images/common/footer/other-social.png'
}

/**
 * 处理社交媒体图标点击事件
 * @param {Object} item - 社交媒体配置项
 * @param {Event} event - 点击事件
 */
const handleSocialClick = (item, event) => {
  if (item.type === 'qrcode') {
    // 显示二维码 Popover
    currentQrcodeImage.value = item.qrcodeImage
    currentQrcodeTitle.value = item.title || '扫码关注'
    // 获取触发元素
    triggerElement.value = event.currentTarget
    showQrcodePopup.value = true
  } else if (item.type === 'link' && item.link) {
    // 跳转到外部链接（只在客户端执行）
    if (typeof window !== 'undefined') {
      window.open(item.link, '_blank')
    }
  }
}

/**
 * 处理二维码图片长按事件（用于放大预览）
 */
const handleQrcodeLongPress = (imageUrl, title) => {
  previewImageUrl.value = imageUrl
  previewImageTitle.value = title
  showImagePreview.value = true
}


/**
 * 加载 Footer 配置
 * 只在客户端环境中执行
 */
const loadFooterConfig = async () => {
  // 确保只在客户端执行
  if (typeof window === 'undefined') {
    return
  }
  
  const profile = __SITE_PROFILE__
  try {
    const modules = import.meta.glob('@/stores/*/footer.js')
    const path = `/src/stores/${profile}/footer.js`
    
    if (modules[path]) {
      const mod = await modules[path]()
      if (mod.default && mod.default.socialMedia) {
        footerConfig.value = mod.default
      }
    } else {
      console.warn(`Footer config for ${profile} not found, falling back to de`)
      // 尝试加载默认配置
      const fallbackMod = await modules['/src/stores/de/footer.js']()
      if (fallbackMod.default && fallbackMod.default.socialMedia) {
        footerConfig.value = fallbackMod.default
      }
    }
  } catch (e) {
    console.error('Failed to load footer config:', e)
  }
}

onMounted(() => {
  loadFooterConfig()
})
</script>

<style scoped lang="scss">
/**
 * Footer 组件样式
 * 使用 CSS 变量统一管理颜色和尺寸，便于维护和主题切换
 */
.footer {
  // CSS 变量定义
  --footer-bg-color: #2c2c2c;
  --footer-text-color: #fff;
  --footer-text-secondary: #999;
  --footer-btn-bg: #444;
  --footer-btn-bg-hover: #555;
  --footer-btn-bg-active: #333;
  
  --footer-padding: 24px;
  --footer-margin-top: 0;
  
  --section-title-size: 18px;
  --section-title-margin: 16px;
  
  --working-hours-size: 13px;
  --working-hours-margin: 16px;
  
  --social-icon-size: 40px;
  --social-icon-gap: 23px;
  
  --contact-btn-padding: 10px 20px;
  --contact-btn-gap: 10px;
  --contact-btn-font: 13px;
  --contact-icon-size: 18px;
  
  --footer-bottom-padding: 20px;
  --footer-info-size: 11px;
  --footer-info-gap: 8px;
  --police-badge-size: 15px;
  
  background-color: var(--footer-bg-color);
  color: var(--footer-text-color);
  padding: var(--footer-padding);
  margin-top: var(--footer-margin-top);
  outline: none;
  border: none;
  
  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 30px;
    outline: none;
    border: none;
    
    .section-title-one {
      font-size: var(--section-title-size);
      font-weight: bold;
      color: var(--footer-text-color);
      margin: 0; // 重置浏览器默认的 margin
      margin-bottom: 16px;
      // margin-bottom: var(--section-title-margin);
      text-align: center;
    }
    .section-title {
      font-size: var(--section-title-size);
      font-weight: bold;
      color: var(--footer-text-color);
      margin: 0; // 重置浏览器默认的 margin
      // margin-bottom: var(--section-title-margin);
      text-align: center;
    }
    
    .working-hours {
      font-size: var(--working-hours-size);
      color: var(--footer-text-secondary);
      margin-bottom: var(--working-hours-margin);
      text-align: center;
    }
    
    .social-icons {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--social-icon-gap);
      flex-wrap: wrap;
      
      .social-icon {
        width: var(--social-icon-size);
        height: var(--social-icon-size);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: transform 0.2s ease;
        
        &:hover {
          transform: scale(1.1);
        }
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }
    
    .contact-buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--contact-btn-gap);
      flex-wrap: nowrap;
      
      .contact-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: var(--contact-btn-padding);
        border-radius: 24px;
        background-color: var(--footer-btn-bg);
        color: var(--footer-text-color);
        border: none;
        font-size: var(--contact-btn-font);
        cursor: pointer;
        transition: background-color 0.2s ease;
        
        &:hover {
          background-color: var(--footer-btn-bg-hover);
        }
        
        &:active {
          background-color: var(--footer-btn-bg-active);
        }
        
        .contact-icon {
          width: var(--contact-icon-size);
          height: var(--contact-icon-size);
          flex-shrink: 0;
          object-fit: contain;
          display: block;
        }
      }
    }
  }
  
  .footer-bottom {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--footer-info-gap);
    
    .footer-info {
      font-size: var(--footer-info-size);
      color: var(--footer-text-secondary);
      text-align: center;
      line-height: 1.6;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--footer-info-gap);
      flex-wrap: wrap;
      
      .police-badge {
        width: var(--police-badge-size);
        height: var(--police-badge-size);
        object-fit: contain;
        flex-shrink: 0;
      }
    }
  }
}
</style>
